/* eslint-disable no-unused-vars */
import { SearchClient } from 'algoliasearch/lite'
import React, { useEffect, useState } from 'react'
import {
  useConfigure,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch-hooks-web'
import { Autocomplete, Button, Place } from '../ui'
import { PlacePropsValue } from '../ui/place'

type AutocompleteItemProps = {
  id: string
  value: string
  highlighted: string
}

type BoxProps = {
  placeholder?: {
    input?: string
    place?: string
  }
  searchClient: SearchClient
  googleMapsApiKey: string
  autoCompleteAttributeName?: string
  // eslint-disable-next-line no-unused-vars
  autoCompleteFilter?: (
    items: AutocompleteItemProps[],
  ) => AutocompleteItemProps[]
}

const AutocompleteItem: React.FC<
  Pick<AutocompleteItemProps, 'highlighted'>
> = ({ highlighted }) => (
  <div className="flex flex-row justify-center">
    <div dangerouslySetInnerHTML={{ __html: highlighted }} />
    <span className="grow" />
  </div>
)

const Box: React.FC<BoxProps> = ({
  placeholder = {},
  googleMapsApiKey,
  searchClient,
  autoCompleteAttributeName,
  autoCompleteFilter,
}) => {
  const { indexUiState, uiState } = useInstantSearch()
  const searchBoxApi = useSearchBox()
  const configureApi = useConfigure({
    ...indexUiState.configure,
    hitsPerPage: 24,
  })

  const [autoCompleteItems, setAutoCompleteItems] = useState<
    AutocompleteItemProps[]
  >([])

  let lat
  let lng

  if (indexUiState.configure?.aroundLatLng) {
    ;[lat, lng] = indexUiState.configure.aroundLatLng.split(',')
  }

  const [place, setPlace] = useState<PlacePropsValue>({
    address: indexUiState.configure?.explain
      ? indexUiState.configure.explain[0]
      : undefined,
    lat: lat ? +lat : undefined,
    lng: lng ? +lng : undefined,
    aroundKm:
      indexUiState.configure?.aroundRadius &&
        indexUiState.configure?.aroundRadius !== 'all'
        ? ((indexUiState.configure.aroundRadius /
          1000) as PlacePropsValue['aroundKm'])
        : undefined,
  })

  const [query, setQuery] = useState(searchBoxApi.query)
  const [clear, setClear] = useState(false)

  const handleSubmit = (val?: string) => {
    if (val) setQuery(val)
    searchBoxApi.refine(val || query)
  }

  useEffect(() => {
    configureApi.refine({
      aroundLatLng:
        place.lat && place.lng ? `${place.lat}, ${place.lng}` : undefined,
      explain: place.address ? [place.address] : [],
      aroundRadius: place.aroundKm ? place.aroundKm * 1000 : undefined,
    })
  }, [place])

  useEffect(() => {
    if (clear) {
      handleSubmit('')
      setClear(false)
    }
  }, [clear])

  if (autoCompleteAttributeName) {
    useEffect(() => {
      searchClient
        .searchForFacetValues([
          {
            indexName: Object.keys(uiState)[0] || '',
            params: {
              facetName: autoCompleteAttributeName,
              facetQuery: query,
              attributesToRetrieve: [autoCompleteAttributeName, '*'],
              maxFacetHits: 10,
              aroundLatLng:
                place.lat && place.lng
                  ? `${place.lat}, ${place.lng}`
                  : undefined,
              aroundRadius: place.aroundKm ? place.aroundKm * 1000 : undefined,
              highlightPreTag: '<em class="search">',
            },
          },
        ])
        .then((results) => {
          let items = results[0].facetHits.map(
            (item): AutocompleteItemProps => ({
              id: item.value,
              value: item.value,
              highlighted: item.highlighted,
            }),
          )

          if (autoCompleteFilter) {
            items = autoCompleteFilter(items)
          }
          setAutoCompleteItems(items)
        })
        .catch((err) => console.error(err))
    }, [query, place])
  }

  return (
    <form
      className="flex flex-col w-full gap-4 lg:flex-row lg:items-center"
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <div className="lg:basis-1/2">
        <Autocomplete<AutocompleteItemProps>
          items={autoCompleteItems}
          placeholder={placeholder?.input}
          value={query}
          onChange={(val) => setQuery(val)}
          icon="search"
          onClear={() => setClear(true)}
          itemComponent={AutocompleteItem}
          onItemSelected={(val) => {
            handleSubmit(val.value)
          }}
        />
      </div>
      <div className="lg:basis-1/2">
        <Place
          googleMapsApiKey={googleMapsApiKey}
          placeholder={placeholder?.place}
          onChange={(val) => setPlace(val)}
          value={place}
        />
      </div>
      <div className="flex flex-row justify-center">
        <Button label="Rechercher" submit color="apply" />
      </div>
    </form>
  )
}

export default Box
